import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Popover from '@mui/material/Popover';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import IconButton from '@mui/material/IconButton';
import { PipelineCandidate, PipelineDraft, PipelineItem, PipelineSection, SECTION_LABELS, PipelineCandidateGroup } from '../types';
import { fetchCandidates, generatePipeline } from '../services/api';
import { comparePipelineDrafts, normalizePipelineTenure } from '../utils/projectOrdering';
import {
  PROJECTS_DATA_COMPACT_FONT_SIZE,
  PROJECTS_DATA_CANDIDATE_PANEL_FLEX,
  PROJECTS_DATA_GRID_TEMPLATE,
  PROJECTS_DATA_HEADER_HEIGHT,
  PROJECTS_DATA_MIN_WIDTH,
  PROJECTS_DATA_PANEL_GAP,
  PROJECTS_DATA_PANEL_WIDTH,
  PROJECTS_DATA_REVIEW_PANEL_FLEX,
  PROJECTS_DATA_ROW_HEIGHT,
  PROJECTS_DATA_DESCRIPTION_CONTENT_GAP,
  PROJECTS_DATA_SECTION_HEADING_GAP,
} from '../components/projectsDataLayout';

const SECTION_OPTIONS: PipelineSection[] = [
  'NORTH', 'NORTH_EAST', 'CENTRAL', 'EAST', 'WEST', 'EC', 'COMMERCIAL', 'INDUSTRIAL', 'COMMERCIAL_INDUSTRIAL',
];

const GENERATE_STAGES = [
  'Snapshotting your selection…',
  'Classifying projects into URA regions…',
  'Laying out cards around the island…',
  'Rendering deck preview…',
];

type CandidateSortField = 'name' | 'district' | 'marketRegion' | 'section';
type CandidateSortDirection = 'default' | 'asc' | 'desc';

const CANDIDATE_SORT_LABELS: Record<CandidateSortField, string> = {
  name: 'Project Name',
  district: 'District',
  marketRegion: 'Region',
  section: 'Area',
};

const COMPACT_FONT_SIZE = PROJECTS_DATA_COMPACT_FONT_SIZE;
const SECTION_HEADING_GAP = PROJECTS_DATA_SECTION_HEADING_GAP;
const DESCRIPTION_CONTENT_GAP = PROJECTS_DATA_DESCRIPTION_CONTENT_GAP;

function itemName(item: Pick<PipelineItem, 'projectName' | 'roadName'>): string {
  return item.projectName?.trim() || item.roadName?.trim() || 'Unnamed project';
}

function draftFromCandidate(c: PipelineCandidate): PipelineDraft {
  const { existingItemId, candidateGroup, ...rest } = c;
  return { ...rest, remarks: null, tenure: normalizePipelineTenure(rest.tenure), itemId: existingItemId };
}

function draftFromItem(key: string, item: PipelineItem): PipelineDraft {
  const { id, sortOrder, ...rest } = item;
  return { key, ...rest, tenure: normalizePipelineTenure(rest.tenure), itemId: id };
}

function keyOfItem(item: PipelineItem): string {
  if (item.sourceType === 'CUSTOM') return `CUSTOM:${item.id}`;
  return item.sourceType === 'PROJECT' ? `P:${item.projectId}` : `L:${item.landSaleId}`;
}

interface InputTabProps {
  items: PipelineItem[];
  onGenerated: (items: PipelineItem[]) => void;
}

interface CandidateRowData {
  candidates: PipelineCandidate[];
  drafts: Map<string, PipelineDraft>;
  onToggle: (candidate: PipelineCandidate) => void;
  tableWidth: number;
}

const CandidateRow = React.memo(({ index, style, data }: ListChildComponentProps<CandidateRowData>) => {
  if (!data) return null;
  const candidate = data.candidates[index];
  if (!candidate) return null;
  const checked = data.drafts.has(candidate.key);
  return (
    <Box
      role="row"
      style={{ ...style, width: data.tableWidth }}
      onClick={() => data.onToggle(candidate)}
      aria-selected={checked}
      sx={{
        display: 'grid',
        gridTemplateColumns: PROJECTS_DATA_GRID_TEMPLATE,
        width: '100%',
        boxSizing: 'border-box',
        alignItems: 'center',
        minWidth: 618,
        height: '100%',
        cursor: 'pointer',
        borderBottom: '1px solid var(--color-border-subtle)',
        '&[aria-selected="true"]': { bgcolor: 'rgba(var(--color-accent-rgb), 0.09)' },
        '&:hover': { bgcolor: 'rgba(var(--color-accent-rgb), 0.06)' },
      }}
    >
      <Box sx={{ minWidth: 0, px: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} noWrap title={itemName(candidate)} sx={{ minWidth: 0, flex: '1 1 auto', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--color-text-strong)', fontSize: COMPACT_FONT_SIZE }}>
            {itemName(candidate)}
          </Typography>
          {candidate.isLaunchingSoon && <Chip size="small" label="Launching Soon!" color="error" sx={{ flexShrink: 0, height: 20, fontSize: 10 }} />}
          {checked && <Chip size="small" label="In deck" color="success" variant="outlined" sx={{ flexShrink: 0, height: 20, fontSize: 10 }} />}
        </Box>
      </Box>
      <Box sx={{ whiteSpace: 'nowrap', px: 0.75, fontSize: COMPACT_FONT_SIZE }}>{candidate.district ?? '—'}</Box>
      <Box sx={{ whiteSpace: 'nowrap', px: 0.75, fontSize: COMPACT_FONT_SIZE }}>{candidate.marketRegion ?? '—'}</Box>
      <Box sx={{ minWidth: 0, px: 0.75 }}><Typography variant="body2" noWrap title={SECTION_LABELS[candidate.section] ?? candidate.section} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', fontSize: COMPACT_FONT_SIZE }}>{SECTION_LABELS[candidate.section] ?? candidate.section}</Typography></Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', px: 0.5 }}>
        <Checkbox
          size="small"
          checked={checked}
          onClick={(event) => event.stopPropagation()}
          onChange={() => data.onToggle(candidate)}
          inputProps={{ 'aria-label': `Select ${itemName(candidate)}` }}
          sx={{ color: 'var(--color-accent)', '&.Mui-checked': { color: 'var(--color-accent)' } }}
        />
      </Box>
    </Box>
  );
});

interface ReviewAccordionProps {
  draft: PipelineDraft;
  expanded: boolean;
  rowRef?: React.Ref<HTMLDivElement>;
  onExpand: (key: string, expanded: boolean) => void;
  onRemove: (key: string) => void;
  updateDraft: (key: string, patch: Partial<PipelineDraft>) => void;
}

/**
 * Keep each editor isolated from updates to other selected projects. The
 * parent still owns the draft map, but React can now skip every unchanged
 * Accordion when one field is being edited.
 */
const ReviewAccordion = React.memo(function ReviewAccordion({
  draft: d,
  expanded,
  rowRef,
  onExpand,
  onRemove,
  updateDraft,
}: ReviewAccordionProps) {
  return (
  <Accordion
    ref={rowRef}
    expanded={expanded}
    onChange={(_, nextExpanded) => onExpand(d.key, nextExpanded)}
    disableGutters
    sx={{ mb: 0.75, bgcolor: 'var(--color-surface-elevated)', borderRadius: 1, overflow: 'hidden', '&:before': { display: 'none' } }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      sx={{
        minHeight: 42,
        px: 1,
        '&.Mui-expanded': { minHeight: 42 },
        '& .MuiAccordionSummary-content': { minWidth: 0, my: 0.75 },
        '& .MuiAccordionSummary-content.Mui-expanded': { my: 0.75 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, width: '100%' }}>
        <Typography
          variant="body2"
          fontWeight={700}
          noWrap
          sx={{ color: 'var(--color-text-strong)', minWidth: 0, flex: '1 1 auto', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.8rem', lineHeight: 1.2 }}
        >
          {itemName(d)}
        </Typography>
        {d.sourceType === 'CUSTOM' && (
          <Chip size="small" label="Custom" color="warning" sx={{ flexShrink: 0, height: 20, fontSize: 10, fontWeight: 700 }} />
        )}
        <Chip size="small" label={SECTION_LABELS[d.section]} sx={{ flexShrink: 0, height: 20, fontSize: 10, fontWeight: 700 }} />
        {d.isLaunchingSoon && (
          <Chip size="small" label="Launching Soon!" color="error" sx={{ flexShrink: 0, height: 20, fontSize: 10, fontWeight: 700 }} />
        )}
        <IconButton
          size="small"
          sx={{ flexShrink: 0 }}
          onClick={(event) => {
            event.stopPropagation();
            onRemove(d.key);
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>
    </AccordionSummary>
    <AccordionDetails
      sx={{
        pt: 0.5, px: 1, fontSize: COMPACT_FONT_SIZE,
        '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: COMPACT_FONT_SIZE },
        '& .MuiInputLabel-root': { fontSize: COMPACT_FONT_SIZE, whiteSpace: 'nowrap', overflow: 'visible' },
        '& .MuiFormLabel-root': { whiteSpace: 'nowrap', overflow: 'visible' },
        '& .MuiOutlinedInput-root': { minWidth: 0 },
        '& .MuiInputBase-input': { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' },
        '& .MuiInputBase-input::placeholder': { fontSize: COMPACT_FONT_SIZE, opacity: 0.7, overflow: 'hidden', textOverflow: 'ellipsis' },
      }}
    >
      <Box sx={{ display: 'grid', minWidth: 0, gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr' }, columnGap: 1, rowGap: 1.5, '& > *': { minWidth: 0 } }}>
      <TextField label="Project Name" size="small" value={d.projectName ?? ''} onChange={(e) => { const projectName = e.target.value; updateDraft(d.key, { projectName: projectName || null, hasProjectName: Boolean(projectName.trim()) }); }} sx={{ gridColumn: '1 / -1' }} />
      <TextField label="Road Name" size="small" value={d.roadName ?? ''} onChange={(e) => updateDraft(d.key, { roadName: e.target.value || null })} sx={{ gridColumn: '1 / -1' }} />
        <TextField label="Remarks" size="small" multiline minRows={2} value={d.remarks ?? ''} onChange={(e) => updateDraft(d.key, { remarks: e.target.value || null })} sx={{ gridColumn: '1 / -1' }} />
        <TextField select label="Status" size="small" value={d.status ?? ''} onChange={(e) => updateDraft(d.key, { status: e.target.value || null })} sx={{ width: { xs: '100%', sm: 190 } }}>
          <MenuItem value="">None</MenuItem>
          <MenuItem value="(FORMER)">(FORMER)</MenuItem>
          <MenuItem value="(REDEVELOPMENT)">(REDEVELOPMENT)</MenuItem>
        </TextField>
        <TextField select label="Area" size="small" value={d.section} onChange={(e) => updateDraft(d.key, { section: e.target.value as PipelineSection })}>
          {SECTION_OPTIONS.map((section) => <MenuItem key={section} value={section}>{SECTION_LABELS[section]}</MenuItem>)}
        </TextField>
        {d.sourceType === 'CUSTOM' && <>
          <TextField label="Latitude" type="number" size="small" value={d.latitude ?? ''} inputProps={{ step: 'any', min: -90, max: 90, inputMode: 'decimal' }} onChange={(e) => updateDraft(d.key, { latitude: e.target.value === '' ? null : Number(e.target.value) })} />
          <TextField label="Longitude" type="number" size="small" value={d.longitude ?? ''} inputProps={{ step: 'any', min: -180, max: 180, inputMode: 'decimal' }} onChange={(e) => updateDraft(d.key, { longitude: e.target.value === '' ? null : Number(e.target.value) })} />
        </>}
        <TextField label="District" size="small" value={d.district ?? ''} onChange={(e) => updateDraft(d.key, { district: e.target.value || null })} />
        <TextField select label="Region" size="small" value={d.marketRegion ?? ''} onChange={(e) => updateDraft(d.key, { marketRegion: (e.target.value || null) as PipelineDraft['marketRegion'] })}>
          <MenuItem value="">Unknown</MenuItem><MenuItem value="CCR">CCR</MenuItem><MenuItem value="RCR">RCR</MenuItem><MenuItem value="OCR">OCR</MenuItem>
        </TextField>
        <TextField label="Tenure" size="small" value={d.tenure ?? ''} onChange={(e) => updateDraft(d.key, { tenure: e.target.value })} onBlur={(e) => updateDraft(d.key, { tenure: normalizePipelineTenure(e.target.value) })} />
        <TextField label="Est. No. of Units" size="small" value={d.estUnits ?? ''} onChange={(e) => updateDraft(d.key, { estUnits: e.target.value || null })} />
        <TextField label="Developer" size="small" value={d.developer ?? ''} onChange={(e) => updateDraft(d.key, { developer: e.target.value || null })} sx={{ gridColumn: '1 / -1' }} />
        <TextField label="Land Cost ($ psf ppr)" size="small" value={d.landCost ?? ''} onChange={(e) => updateDraft(d.key, { landCost: e.target.value || null })} />
        <TextField label="Est. Avg Launch Price" size="small" value={d.estLaunchPrice ?? ''} placeholder="$2,400 psf" sx={{ gridColumn: { xs: '1 / -1', md: 'span 2' }, minWidth: 0, width: '100%' }} onChange={(e) => updateDraft(d.key, { estLaunchPrice: e.target.value || null })} />
        <TextField label="Target Preview" size="small" value={d.targetPreview ?? ''} placeholder="1Q 2027" onChange={(e) => updateDraft(d.key, { targetPreview: e.target.value || null })} />
        <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', minHeight: 40, gap: 2, flexWrap: 'wrap' }}>
          <FormControlLabel sx={{ m: 0, minHeight: 40, alignItems: 'center' }} control={<Switch size="small" checked={d.isLaunchingSoon} onChange={(e) => updateDraft(d.key, { isLaunchingSoon: e.target.checked })} />} label={<Typography variant="caption" fontWeight={700}>Launching Soon!</Typography>} />
          <FormControlLabel sx={{ m: 0, minHeight: 40, alignItems: 'center' }} control={<Switch size="small" checked={d.landAwarded} onChange={(e) => updateDraft(d.key, { landAwarded: e.target.checked })} />} label={<Typography variant="caption" fontWeight={700}>Land Awarded</Typography>} />
          <FormControlLabel sx={{ m: 0, minHeight: 40, alignItems: 'center' }} control={<Switch size="small" checked={d.previewConfirmed} onChange={(e) => updateDraft(d.key, { previewConfirmed: e.target.checked })} />} label={<Typography variant="caption" fontWeight={700}>Preview Confirmed</Typography>} />
        </Box>
      </Box>
    </AccordionDetails>
  </Accordion>
  );
});

export const InputTab: React.FC<InputTabProps> = ({ items, onGenerated }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<PipelineCandidate[]>([]);
  const [landSales, setLandSales] = useState<PipelineCandidate[]>([]);
  const [sourceTab, setSourceTab] = useState<0 | 1 | 2 | 3>(0);
  const [search, setSearch] = useState('');
  const [candidateFilters, setCandidateFilters] = useState<{
    regions: string[];
    districts: string[];
    areas: PipelineSection[];
  }>({ regions: [], districts: [], areas: [] });
  const [candidateSort, setCandidateSort] = useState<{
    field: CandidateSortField | null;
    direction: CandidateSortDirection;
  }>({ field: null, direction: 'default' });
  const [candidateHeaderPopover, setCandidateHeaderPopover] = useState<{
    element: HTMLElement;
    field: CandidateSortField;
  } | null>(null);
  const [drafts, setDrafts] = useState<Map<string, PipelineDraft>>(new Map());
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  // Generate progress theatre
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState(GENERATE_STAGES[0]);
  const progressTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const reviewDetailsRef = useRef<HTMLDivElement | null>(null);
  const candidateListRef = useRef<FixedSizeList<CandidateRowData> | null>(null);
  const candidateTableRef = useRef<HTMLDivElement | null>(null);
  const [candidateTableWidth, setCandidateTableWidth] = useState(PROJECTS_DATA_MIN_WIDTH);
  const reviewRowRefs = useRef(new Map<string, HTMLDivElement>());
  const reviewRowRefCallbacks = useRef(new Map<string, (node: HTMLDivElement | null) => void>());
  const pendingReviewScrollKeyRef = useRef<string | null>(null);

  // Seed drafts from already-saved deck items so re-entry keeps prior edits.
  useEffect(() => {
    setDrafts((prev) => {
      const next = new Map(prev);
      for (const item of items) {
        const key = keyOfItem(item);
        const alreadyTracked = item.sourceType === 'CUSTOM'
          && Array.from(next.values()).some((draft) => draft.sourceType === 'CUSTOM' && draft.itemId === item.id);
        if (!next.has(key) && !alreadyTracked) next.set(key, draftFromItem(key, item));
      }
      return next;
    });
  }, [items]);

  useEffect(() => {
    let mounted = true;
    const startedAt = performance.now();
    (async () => {
      try {
        const data = await fetchCandidates();
        if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_DEBUG_SUPER_PIPELINE_TIMING === 'true') {
          console.debug('[project-deck] candidate fetch', {
            elapsedMs: Math.round(performance.now() - startedAt),
            projects: data.projects.length,
            landSales: data.landSales.length,
          });
        }
        if (!mounted) return;
        setProjects(data.projects);
        setLandSales(data.landSales);
        setError(null);
        if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_DEBUG_SUPER_PIPELINE_TIMING === 'true') {
          requestAnimationFrame(() => console.debug('[project-deck] review list rendered', {
            elapsedMs: Math.round(performance.now() - startedAt),
          }));
        }
      } catch (e) {
        if (mounted) setError(e instanceof Error ? e.message : 'Failed to load candidates');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => () => {
    if (progressTimer.current) clearInterval(progressTimer.current);
  }, []);

  const list = useMemo(() => {
    const group: PipelineCandidateGroup = sourceTab === 0
      ? 'UPCOMING'
      : sourceTab === 1 ? 'GLS' : sourceTab === 2 ? 'REDEVELOPMENT' : 'ENBLOC';
    return group === 'GLS' ? landSales : projects.filter((candidate) => candidate.candidateGroup === group);
  }, [landSales, projects, sourceTab]);

  // Filter options are derived from the complete candidate pool, never from
  // the active tab or current search results. This keeps the dropdown values
  // stable while users refine the list and move between Upcoming, GLS,
  // Redevelopment, and Enbloc.
  const candidateFilterOptions = useMemo(() => {
    const allCandidates = [...projects, ...landSales];
    const uniqueSorted = (values: Array<string | null | undefined>) => Array.from(new Set(
      values.map((value) => value?.trim()).filter((value): value is string => Boolean(value)),
    )).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    const areas = SECTION_OPTIONS.filter((section) => allCandidates.some((candidate) => candidate.section === section));
    return {
      regions: uniqueSorted(allCandidates.map((candidate) => candidate.marketRegion)),
      districts: uniqueSorted(allCandidates.map((candidate) => candidate.district)),
      areas,
    };
  }, [landSales, projects]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const selectedRegions = new Set(candidateFilters.regions);
    const selectedDistricts = new Set(candidateFilters.districts);
    const selectedAreas = new Set(candidateFilters.areas);
    const matching = list.filter((c) => {
      if (selectedRegions.size > 0 && !selectedRegions.has(c.marketRegion ?? '')) return false;
      if (selectedDistricts.size > 0 && !selectedDistricts.has(c.district ?? '')) return false;
      if (selectedAreas.size > 0 && !selectedAreas.has(c.section)) return false;
      if (!q) return true;
      const areaLabel = SECTION_LABELS[c.section] ?? c.section;
      return [itemName(c), c.developer, c.district, c.status, c.marketRegion, c.section, areaLabel]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });

    const sortField = candidateSort.field;
    if (!sortField || candidateSort.direction === 'default') return matching;
    const direction = candidateSort.direction === 'asc' ? 1 : -1;
    const valueFor = (candidate: PipelineCandidate): string => {
      if (sortField === 'section') return SECTION_LABELS[candidate.section] ?? candidate.section;
      if (sortField === 'name') return itemName(candidate);
      return String(candidate[sortField] ?? '');
    };
    return matching
      .map((candidate, sourceIndex) => ({ candidate, sourceIndex }))
      .sort((a, b) => {
        const comparison = valueFor(a.candidate).localeCompare(
          valueFor(b.candidate),
          undefined,
          { numeric: true, sensitivity: 'base' },
        );
        if (comparison !== 0) return comparison * direction;
        if (a.sourceIndex !== b.sourceIndex) return a.sourceIndex - b.sourceIndex;
        return a.candidate.key.localeCompare(b.candidate.key, undefined, { sensitivity: 'base' });
      })
      .map(({ candidate }) => candidate);
  }, [candidateFilters, candidateSort, list, search]);

  const activeFilterCount = candidateFilters.regions.length
    + candidateFilters.districts.length
    + candidateFilters.areas.length;
  const hasCandidateControls = activeFilterCount > 0 || search.trim().length > 0;

  useLayoutEffect(() => {
    // Filtering, changing tabs, or sorting changes the result set. Always
    // show the first result after those changes, but leave selection clicks
    // alone so selecting a row never moves the user's scroll position.
    candidateListRef.current?.scrollTo(0);
  }, [candidateFilters, candidateSort, search, sourceTab]);

  useLayoutEffect(() => {
    const element = candidateTableRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const updateWidth = () => {
      const nextWidth = Math.max(PROJECTS_DATA_MIN_WIDTH, Math.round(element.clientWidth));
      setCandidateTableWidth((current) => current === nextWidth ? current : nextWidth);
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const renderCandidateSortIcon = (field: CandidateSortField) => {
    const direction = candidateSort.field === field ? candidateSort.direction : 'default';
    if (direction === 'asc') {
      return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.15 }}>
          <ArrowUpwardIcon sx={{ fontSize: '0.85rem' }} />
          <MenuIcon sx={{ fontSize: '0.9rem' }} />
        </Box>
      );
    }
    if (direction === 'desc') {
      return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.15 }}>
          <ArrowDownwardIcon sx={{ fontSize: '0.85rem' }} />
          <MenuIcon sx={{ fontSize: '0.9rem' }} />
        </Box>
      );
    }
    return <MenuIcon sx={{ fontSize: '0.9rem' }} />;
  };

  const renderCandidateHeader = (field: CandidateSortField, label: string) => (
    <Box sx={{ minWidth: 0, width: '100%', minHeight: PROJECTS_DATA_HEADER_HEIGHT, boxSizing: 'border-box', display: 'flex', alignItems: 'center', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', bgcolor: 'var(--color-surface-elevated)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
        <Typography component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.8rem', fontWeight: 800, whiteSpace: 'nowrap', flex: '1 1 auto' }}>
          {label}
        </Typography>
        <IconButton
          size="small"
          aria-label={`Sort by ${label}`}
          title={`Sort by ${label}`}
          onClick={(event) => setCandidateHeaderPopover({ element: event.currentTarget, field })}
          sx={{
            p: 0.35,
            flexShrink: 0,
            color: candidateSort.field === field && candidateSort.direction !== 'default'
              ? 'var(--color-accent)'
              : 'var(--color-text-muted)',
            '&:hover': { color: 'var(--color-accent)', bgcolor: 'rgba(var(--color-accent-rgb), 0.1)' },
          }}
        >
          {renderCandidateSortIcon(field)}
        </IconButton>
      </Box>
    </Box>
  );

  const renderMultiSelectFilter = (
    label: string,
    options: string[],
    value: string[],
    onChange: (next: string[]) => void,
    getOptionLabel: (option: string) => string = (option) => option,
  ) => {
    const selectAllValue = '__SELECT_ALL__';
    const allSelected = options.length > 0 && value.length === options.length;
    const partiallySelected = value.length > 0 && !allSelected;
    return (
      <Box sx={{ minWidth: 0 }}>
        {renderFilterCaption(label)}
        <FormControl fullWidth size="small" sx={{ minWidth: 0 }}>
          <Select
            multiple
            displayEmpty
            value={value}
            inputProps={{ 'aria-label': `${label} filter` }}
            onChange={(event) => {
              const rawValue = event.target.value as string | string[];
              const next = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;
              if (next.includes(selectAllValue)) {
                onChange(allSelected ? [] : [...options]);
              } else {
                onChange(next.filter((option) => option !== selectAllValue));
              }
            }}
            renderValue={(selected) => {
              const selectedValues = selected as string[];
              return selectedValues.length === 0 ? 'All' : `${selectedValues.length} selected`;
            }}
            MenuProps={{
              disableAutoFocusItem: true,
              MenuListProps: {
                disablePadding: true,
                sx: {
                  '& > .MuiMenuItem-root:first-of-type': {
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    bgcolor: 'var(--color-surface-elevated)',
                  },
                },
              },
              PaperProps: {
                sx: {
                  maxHeight: 360,
                  '& .MuiMenuItem-root, & .MuiTypography-root': { fontSize: COMPACT_FONT_SIZE },
                },
              },
            }}
            sx={{
              width: '100%',
              minHeight: 48,
              minWidth: 0,
              overflow: 'hidden',
              bgcolor: 'var(--color-surface)',
              '& .MuiSelect-select': {
                minWidth: 0,
                py: 1.35,
                fontSize: COMPACT_FONT_SIZE,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          >
            <MenuItem value={selectAllValue} sx={{ minHeight: 40 }}>
              <Checkbox size="small" checked={allSelected} indeterminate={partiallySelected} />
              <Typography component="span" sx={{ fontWeight: 700, fontSize: COMPACT_FONT_SIZE }}>Select All</Typography>
            </MenuItem>
            {options.map((option) => (
              <MenuItem key={option} value={option} sx={{ minHeight: 40 }}>
                <Checkbox size="small" checked={value.includes(option)} />
                <Typography component="span" sx={{ fontSize: COMPACT_FONT_SIZE }}>{getOptionLabel(option)}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  const navigateToReview = useCallback((key: string) => {
    pendingReviewScrollKeyRef.current = key;
    setExpandedKey(key);
  }, []);

  const toggle = useCallback((c: PipelineCandidate) => {
    const wasSelected = drafts.has(c.key);
    setDrafts((prev) => {
      const next = new Map(prev);
      if (next.has(c.key)) {
        next.delete(c.key);
      } else {
        // Candidate data is intentionally cached between generations. Resolve
        // the database identity from the current active deck instead of using
        // a potentially stale existingItemId from that cache.
        const active = items.find((item) => c.sourceType === 'PROJECT'
          ? item.sourceType === 'PROJECT' && item.projectId === c.projectId
          : item.sourceType === 'LANDSALE' && item.landSaleId === c.landSaleId);
        next.set(c.key, { ...draftFromCandidate(c), itemId: active?.id ?? null });
      }
      return next;
    });
    if (!wasSelected) navigateToReview(c.key);
  }, [drafts, items, navigateToReview]);

  const addCustomProject = () => {
    const key = `CUSTOM:${Date.now()}`;
    const custom: PipelineDraft = {
      key,
      itemId: null,
      sourceType: 'CUSTOM',
      projectId: null,
      landSaleId: null,
      projectName: null,
      roadName: null,
      remarks: null,
      status: null,
      section: 'CENTRAL',
      district: null,
      tenure: null,
      estUnits: null,
      developer: null,
      landCost: null,
      estLaunchPrice: null,
      targetPreview: null,
      isLaunchingSoon: false,
      hasProjectName: false,
      landAwarded: true,
      previewConfirmed: false,
      latitude: null,
      longitude: null,
      region: null,
      marketRegion: null,
    };
    setDrafts((prev) => new Map(prev).set(key, custom));
    navigateToReview(key);
  };

  useEffect(() => {
    if (pendingReviewScrollKeyRef.current !== expandedKey) return undefined;
    const timer = window.setTimeout(() => {
      const row = expandedKey ? reviewRowRefs.current.get(expandedKey) : null;
      if (row) {
        row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        row.querySelector<HTMLInputElement>('input:not([type="hidden"]), textarea')?.focus();
      } else {
        const container = reviewDetailsRef.current;
        if (container) container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }
      pendingReviewScrollKeyRef.current = null;
    }, 80);
    return () => window.clearTimeout(timer);
  }, [expandedKey]);

  const updateDraft = useCallback((key: string, patch: Partial<PipelineDraft>) => {
    setDrafts((prev) => {
      const next = new Map(prev);
      const cur = next.get(key);
      if (cur) next.set(key, { ...cur, ...patch });
      return next;
    });
  }, []);

  const handleReviewExpand = useCallback((key: string, expanded: boolean) => {
    setExpandedKey(expanded ? key : null);
  }, []);

  const getReviewRowRef = useCallback((key: string) => {
    const existing = reviewRowRefCallbacks.current.get(key);
    if (existing) return existing;
    const callback = (node: HTMLDivElement | null) => {
      if (node) reviewRowRefs.current.set(key, node);
      else reviewRowRefs.current.delete(key);
    };
    reviewRowRefCallbacks.current.set(key, callback);
    return callback;
  }, []);

  const removeDraft = useCallback((key: string) => {
    setDrafts((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const selected = useMemo(() => Array.from(drafts.values()).sort(comparePipelineDrafts), [drafts]);

  const handleGenerate = async () => {
    const invalidName = selected.find((item) => !item.projectName?.trim() && !item.roadName?.trim());
    if (invalidName) {
      setError(`Enter a Project Name or Road Name for ${itemName(invalidName)}.`);
      setExpandedKey(invalidName.key);
      return;
    }
    const invalidCoordinates = selected.find((item) => {
      if (item.sourceType !== 'CUSTOM') return false;
      const latitude = item.latitude == null ? null : Number(item.latitude);
      const longitude = item.longitude == null ? null : Number(item.longitude);
      return latitude == null || longitude == null
        || !Number.isFinite(latitude) || latitude < -90 || latitude > 90
        || !Number.isFinite(longitude) || longitude < -180 || longitude > 180;
    });
    if (invalidCoordinates) {
      setError(`Enter valid latitude (-90 to 90) and longitude (-180 to 180) for ${itemName(invalidCoordinates)}.`);
      setExpandedKey(invalidCoordinates.key);
      return;
    }
    setGenerating(true);
    setProgress(4);
    setStage(GENERATE_STAGES[0]);
    const startedAt = Date.now();
    progressTimer.current = setInterval(() => {
      setProgress((p) => (p < 88 ? p + Math.max(0.6, (88 - p) * 0.045) : p));
      const elapsed = Date.now() - startedAt;
      setStage(GENERATE_STAGES[Math.min(Math.floor(elapsed / 900), GENERATE_STAGES.length - 1)]);
    }, 140);
    try {
      const activeIds = new Set(items.map((item) => item.id));
      const generationSelection = selected.map((draft) => ({
        ...draft,
        itemId: draft.itemId != null && activeIds.has(draft.itemId) ? draft.itemId : null,
      }));
      const saved = await generatePipeline(generationSelection);
      // Keep the review draft keys stable while attaching the database IDs
      // returned by the upsert endpoint. Custom projects have no source ID,
      // so match them deterministically in their selected order.
      const savedBySource = new Map(saved
        .filter((item) => item.sourceType !== 'CUSTOM')
        .map((item) => [item.sourceType === 'PROJECT' ? `P:${item.projectId}` : `L:${item.landSaleId}`, item]));
      const savedCustom = saved.filter((item) => item.sourceType === 'CUSTOM');
      let customIndex = 0;
      setDrafts((previous) => {
        const next = new Map(previous);
        selected.forEach((draft) => {
          const savedItem = draft.sourceType === 'CUSTOM'
            ? savedCustom[customIndex++]
            : savedBySource.get(draft.sourceType === 'PROJECT' ? `P:${draft.projectId}` : `L:${draft.landSaleId}`);
          if (savedItem) next.set(draft.key, { ...draft, itemId: savedItem.id });
        });
        return next;
      });
      setProgress(100);
      setStage('Deck ready!');
      setTimeout(() => {
        setGenerating(false);
        // Switch to Overview only after the progress bar has reached 100% and
        // the ready state has been visible to the user.
        onGenerated(saved);
      }, 550);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate deck');
      setGenerating(false);
    } finally {
      if (progressTimer.current) {
        clearInterval(progressTimer.current);
        progressTimer.current = null;
      }
    }
  };

  const renderFilterCaption = (label: string) => (
    <Typography
      variant="caption"
      sx={{ color: 'var(--color-text-strong)', fontWeight: 600, fontSize: COMPACT_FONT_SIZE, mb: 0.5, display: 'block' }}
    >
      {label}
    </Typography>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10, gap: 2 }}>
        <CircularProgress />
        <Typography sx={{ color: 'var(--color-text-muted)' }}>Loading upcoming projects & land sales…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: PROJECTS_DATA_PANEL_GAP, flexDirection: { xs: 'column', lg: 'row' }, alignItems: 'flex-start' }}>
      {/* ── Candidate picker ── */}
      <Paper sx={{ flex: PROJECTS_DATA_CANDIDATE_PANEL_FLEX, minWidth: 0, p: 2, bgcolor: 'var(--color-surface)', width: PROJECTS_DATA_PANEL_WIDTH }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: 'var(--color-text-strong)', mb: SECTION_HEADING_GAP }}>
          1 · Choose what goes on the map
        </Typography>
        <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mb: DESCRIPTION_CONTENT_GAP }}>
          Unsold Singapore projects and all HuttonPortal GLS land sales. Unawarded sites appear first, followed by award date from newest to oldest.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <Tabs value={sourceTab} onChange={(_, v) => setSourceTab(v as 0 | 1 | 2 | 3)} sx={{ mb: 1.5, minHeight: 38 }}>
          <Tab label={`Upcoming Projects (${projects.filter((candidate) => candidate.candidateGroup === 'UPCOMING').length})`} sx={{ minHeight: 38, textTransform: 'none', fontWeight: 600 }} />
          <Tab label={`GLS (${landSales.length})`} sx={{ minHeight: 38, textTransform: 'none', fontWeight: 600 }} />
          <Tab label={`Redevelopment (${projects.filter((candidate) => candidate.candidateGroup === 'REDEVELOPMENT').length})`} sx={{ minHeight: 38, textTransform: 'none', fontWeight: 600 }} />
          <Tab label={`Enbloc (${projects.filter((candidate) => candidate.candidateGroup === 'ENBLOC').length})`} sx={{ minHeight: 38, textTransform: 'none', fontWeight: 600 }} />
        </Tabs>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 1.5 }}>
          <TextField
            fullWidth
            size="medium"
            placeholder="Search project, developer, district, region, area…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': { minHeight: 48, bgcolor: 'var(--color-surface)' },
              '& .MuiInputBase-input': { fontSize: COMPACT_FONT_SIZE },
              '& .MuiInputBase-input::placeholder': { fontSize: COMPACT_FONT_SIZE },
            }}
          />
          <Box sx={{ display: 'grid', minWidth: 0, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr)) minmax(112px, 0.34fr)' }, gap: 1.25, alignItems: 'start' }}>
            {renderMultiSelectFilter(
              'Region',
              candidateFilterOptions.regions,
              candidateFilters.regions,
              (next) => setCandidateFilters((current) => ({ ...current, regions: next })),
            )}
            {renderMultiSelectFilter(
              'District',
              candidateFilterOptions.districts,
              candidateFilters.districts,
              (next) => setCandidateFilters((current) => ({ ...current, districts: next })),
            )}
            {renderMultiSelectFilter(
              'Area',
              candidateFilterOptions.areas,
              candidateFilters.areas,
              (next) => setCandidateFilters((current) => ({ ...current, areas: next as PipelineSection[] })),
              (option) => SECTION_LABELS[option as PipelineSection] ?? option,
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, justifyContent: 'flex-end', minWidth: 0, minHeight: 70, overflow: 'hidden' }}>
              <Button
                size="small"
                variant="text"
                disabled={!hasCandidateControls}
                onClick={() => {
                  setSearch('');
                  setCandidateFilters({ regions: [], districts: [], areas: [] });
                }}
                aria-label="Clear all search filters"
                title="Clear all search filters"
                sx={{ alignSelf: 'stretch', justifyContent: 'flex-start', minWidth: 0, width: '100%', px: 0.5, textTransform: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.82rem' }}
              >
                Clear filters
              </Button>
            </Box>
          </Box>
        </Box>
        <Box ref={candidateTableRef} sx={{ maxHeight: { lg: 'calc(100vh - 340px)' }, overflowX: 'auto', overflowY: 'auto', pr: 0.5, minWidth: 0 }}>
          <Box sx={{ width: candidateTableWidth, minWidth: PROJECTS_DATA_MIN_WIDTH, display: 'grid', gridTemplateColumns: PROJECTS_DATA_GRID_TEMPLATE, boxSizing: 'border-box', bgcolor: 'var(--color-surface-elevated)', '& > *': { px: 0.75, fontSize: COMPACT_FONT_SIZE, boxSizing: 'border-box' } }} aria-label="Projects and data candidates" role="row">
            {renderCandidateHeader('name', 'Project Name')}
            {renderCandidateHeader('district', 'District')}
            {renderCandidateHeader('marketRegion', 'Region')}
            {renderCandidateHeader('section', 'Area')}
            <Box sx={{ minHeight: PROJECTS_DATA_HEADER_HEIGHT }} />
          </Box>
          {filtered.length > 0 ? (
            <FixedSizeList
              ref={candidateListRef}
              height={520}
              itemCount={filtered.length}
              itemSize={PROJECTS_DATA_ROW_HEIGHT}
              width={candidateTableWidth}
              overscanCount={8}
              itemData={{ candidates: filtered, drafts, onToggle: toggle, tableWidth: candidateTableWidth }}
            >
              {CandidateRow}
            </FixedSizeList>
          ) : (
            <Box sx={{ width: candidateTableWidth, minWidth: PROJECTS_DATA_MIN_WIDTH, py: 3, textAlign: 'center', color: 'var(--color-text-muted)', fontSize: COMPACT_FONT_SIZE }}>
              Nothing matches “{search}”.
            </Box>
          )}
        </Box>
        <Popover
          open={Boolean(candidateHeaderPopover)}
          anchorEl={candidateHeaderPopover?.element}
          onClose={() => setCandidateHeaderPopover(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {candidateHeaderPopover && (
            <Box sx={{ p: 1.5, minWidth: 220 }}>
              <Typography variant="body2" sx={{ color: 'var(--color-info)', fontWeight: 600, mb: 1, fontSize: '0.82rem' }}>
                Sort {CANDIDATE_SORT_LABELS[candidateHeaderPopover.field]}
              </Typography>
              <ToggleButtonGroup
                value={candidateSort.field === candidateHeaderPopover.field ? candidateSort.direction : 'default'}
                exclusive
                fullWidth
                size="small"
                onChange={(_, value: CandidateSortDirection | null) => {
                  if (!value) return;
                  setCandidateSort(value === 'default'
                    ? { field: null, direction: 'default' }
                    : { field: candidateHeaderPopover.field, direction: value });
                  setCandidateHeaderPopover(null);
                }}
                sx={{
                  '& .MuiToggleButton-root': {
                    flex: 1,
                    py: 0.65,
                    px: 0.75,
                    fontSize: '0.78rem',
                    textTransform: 'none',
                  },
                }}
              >
                <ToggleButton value="default">Default</ToggleButton>
                <ToggleButton value="asc">Asc</ToggleButton>
                <ToggleButton value="desc">Desc</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
        </Popover>
      </Paper>

      {/* ── Selected + editable fields ── */}
      <Paper sx={{ flex: PROJECTS_DATA_REVIEW_PANEL_FLEX, minWidth: 0, p: 1.5, bgcolor: 'var(--color-surface)', width: PROJECTS_DATA_PANEL_WIDTH, position: { lg: 'sticky' }, top: { lg: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: SECTION_HEADING_GAP, gap: 1.25 }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: 'var(--color-text-strong)', fontSize: '1.05rem' }}>
            2 · Review the details
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addCustomProject}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              Add custom project
            </Button>
            <Chip label={`${selected.length} selected`} size="small" sx={{ fontWeight: 700 }} />
          </Box>
        </Box>
        <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mb: DESCRIPTION_CONTENT_GAP, fontSize: COMPACT_FONT_SIZE }}>
          Auto-filled from Project & Land Sale records — adjust anything before generating.
        </Typography>

        <Box ref={reviewDetailsRef} sx={{ maxHeight: { lg: 'calc(100vh - 380px)' }, overflowY: 'auto', pr: 0.5, mt: 0.5 }}>
          {selected.length === 0 && (
            <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', py: 4, textAlign: 'center' }}>
              Select projects on the left to start building the deck.
            </Typography>
          )}
          {selected.map((draft) => (
            <ReviewAccordion
              key={draft.key}
              draft={draft}
              expanded={expandedKey === draft.key}
              rowRef={getReviewRowRef(draft.key)}
              onExpand={handleReviewExpand}
              onRemove={removeDraft}
              updateDraft={updateDraft}
            />
          ))}
        </Box>

        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={selected.length === 0 || generating}
          onClick={handleGenerate}
          startIcon={<AutoAwesomeIcon />}
          sx={{ mt: 2, fontWeight: 800, textTransform: 'none', py: 1.2 }}
        >
          Generate Deck Preview
        </Button>
      </Paper>

      {/* ── Generate progress theatre ── */}
      <Dialog open={generating} maxWidth="sm" fullWidth>
        <DialogContent sx={{ py: 4, textAlign: 'center' }}>
          <AutoAwesomeIcon sx={{ fontSize: 40, color: 'var(--color-accent)', mb: 1 }} />
          <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
            Generating your Pipeline deck
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mb: 2.5 }}>
            {stage}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(progress, 100)}
            sx={{
              height: 10,
              borderRadius: 5,
              '& .MuiLinearProgress-bar': {
                borderRadius: 5,
                background: 'linear-gradient(90deg, var(--color-accent), #f5d76e)',
              },
            }}
          />
          <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', mt: 1, display: 'block' }}>
            {Math.round(Math.min(progress, 100))}%
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
